/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { getConfig } from "../../config";

const ENDPOINTS = {

  GET_DOMAIN_APPROVER_ALIAS_ID: () => {
    return `/v1/admins/mpl/dictionaries/domains/alias`;
  },
  POST_EXPORT_TO_EXCEL: () => {
    return `/v1/admins/global-admin/mpl/applications/adminreport/download`;
  },

  POST_ADD_MORE_REPORTS: (appId, submissionId, reportComment) => {
    return `/v1/admins/mpl/applications/${appId}/submissions/${submissionId}/qareports?comment=${reportComment}`;
  },

  POST_SUBMIT_REASSIGN_OWNERSHIP: (applicationId, payloadId) => {
    return `/v1/admins/global-admin/mpl/applications/${applicationId}?newSellerWso2Id=${payloadId}`;
  },

ADD_ECU_TYPE_DOMAIN_APPROVER: (makeName, ecuType) => {
    return `/v1/admins/mpl/dictionaries/ecus?makeName=${makeName}&ecuType=${ecuType}`;
  },

DELETE_ECU_TYPE_DOMAIN_APPROVER: (makeName, ecuType) => {
    return `/v1/admins/mpl/dictionaries/ecus?makeName=${makeName}&ecuType=${ecuType}`;
  },

  GET_APPLICATION: (applicationId) => {
    if (
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      return `/v1/admins/mpl/applications/${applicationId}`;
    } else {
      return `/v2/users/mpl/applications/${applicationId}`;
    }
  },
  GET_SUBMISSION: (applicationId, submissionId) => {
    if (
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      return `/v1/admins/mpl/applications/${applicationId}/submissions/${submissionId}`;
    } else {
      return `/v2/users/mpl/applications/${applicationId}/submissions/${submissionId}`;
    }
  },
  GET_SUBMISSION_CHANGELOG: (applicationId, submissionId) => {
    return `/v1/users/mpl/applications/${applicationId}/submissions/${submissionId}/changelog`;
  },
  GET_ADMIN_SUBMISSION_CHANGELOG: (applicationId, submissionId) => {
    return `/v1/admins/mpl/applications/${applicationId}/submissions/${submissionId}/changelog`;
  },
  GET_CATEGORIES: () => {
    if (
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      return `/v1/admins/mpl/dictionaries/categories`;
    } else {
      return `/v1/users/mpl/dictionaries/categories`;
    }
  },
  GET_TEAMS: (teamId) => {
    return `/v1/teams/${teamId}`;
  },
  GET_LANGUAGES: (defaultLanguage) => {
    if (
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      return `/v1/admins/mpl/dictionaries/languages?defaultLanguage=${defaultLanguage}`;
    } else {
      return `/v1/users/mpl/dictionaries/languages?defaultLanguage=${defaultLanguage}`;
    }
  },

  PUT_APK_VALIDATION: (applicationId, submissionId) => {
    return `/v1/admins/mpl/applications/${applicationId}/submissions/${submissionId}/apkvalidation`;
  },

  PUT_SUBMISSION: (applicationId, submissionId) => {
    return `/v1/users/mpl/applications/${applicationId}/submissions/${submissionId}`;
  },

  PUT_GLOBAL_ADMIN_GARAGE_MODE_SUBMISSION: (applicationId, submissionId) => {
    return `/v1/admins/mpl/applications/${applicationId}/submissions/${submissionId}`;
  },
  GET_ALL_TEAMS: () => {
    return `/v1/teams`;
  },
  POST_CREATE_APP: (appName, selectedLang, selectedTeam) => {
    return `/v2/users/mpl/applications?applicationName=${appName}&defaultLanguage=${selectedLang}&teamId=${selectedTeam}&enableServiceIds=false`;
  },
  GET_ALL_APPLICATIONS: (status) => {
    if (getConfig().REACT_APP_PORTAL === "seller" && status === undefined)
      return `/v2/users/mpl/applications`;
    else return `/v1/admins/mpl/applications?status=${status}`;
  },
  GET_DELETED_APPLICATIONS: () => {
    return `/v1/admins/mpl/applications/deleted`;
  },
  DELETE_DELETED_APPLICATION: (applicationId) => {
    return `/v1/admins/mpl/applications/${applicationId}`;
  },
  GET_ALL_PUBLISHER_IDS: () => {
    return `/v1/admins/mpl/cognito/users/filter?sortOrder=ASC`;
  },
  REJECT_DELETED_APPLICATION: (applicationId) => {
    return `/v1/admins/mpl/applications/${applicationId}/reject`;
  },
  GET_ALL_APPLICATIONS_STATUS: (status) => {
    return `/v2/users/mpl/applications?status=${status}`;
  },
  GET_APP_OEM_STATUS: (packageId, appVersion) => {
    return `/v1/users/mpl/reports/app_oem_status/${packageId}?appVersion=${appVersion}`;
  },
  POST_CREATE_FEATURE: (
    featureName,
    selectedLang,
    selectedTeam,
    featureUniqueId
  ) => {
    return `/v1/users/mpl/feature?featureName=${featureName}&defaultLanguage=${selectedLang}&teamId=${selectedTeam}&featureUniqueId=${featureUniqueId}`;
  },
  GET_ALL_FEATURES: () => {
    return `/v1/users/mpl/feature`;
  },
  GET_ALL_FEATURES_STATUS: (status) => {
    return `/v1/users/mpl/feature?status=${status}`;
  },

  GET_COUNTRIES: () => {
    if (
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      return `/v1/admins/mpl/dictionaries/countries?cvaaexclusion=false`;
    } else {
      return `/v1/users/mpl/dictionaries/countries?cvaaexclusion=false`;
    }
  },
  GET_VEHICLES: () => {
    return `/v1/users/mpl/dictionaries/vehicles`;
  },
  GET_DOMAIN_APPROVER_VEHICLES: (domainAliasId) => {
    return `/v1/admins/mpl/dictionaries/vehicles?domainAliasId=${domainAliasId}`;
  },
  GET_ALL_IDS: () => {
    return `/v1/portfoliomanager/portfolios/ids`;
  },
  GET_CPUS: () => {
    if (
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      return `/v1/admins/mpl/dictionaries/cpus`;
    } else {
      return `/v1/users/mpl/dictionaries/cpus`;
    }
  },

  GET_OEM_ADMIN_MMY: (appId) => {
    return `/v1/portfoliomanager/portfolios/${sessionStorage.getItem(
      "portfolioId"
    )}/contents/${appId}/mmy`;
  },
  POST_SCREENSHOT: (applicationId, submissionId, defaultLanguage) => {
    return `/v1/users/mpl/applications/${applicationId}/submissions/${submissionId}/screenshots?language=${defaultLanguage}`;
  },
  DELETE_SCREENSHOT: (
    applicationId,
    submissionId,
    screenshotId,
    defaultLanguage
  ) => {
    return `/v1/users/mpl/applications/${applicationId}/submissions/${submissionId}/screenshots/${screenshotId}?language=${defaultLanguage}`;
  },
  GET_COGNITO_SELF: () => {
    return `/v1/users/mpl/cognito/self`;
  },
  PATCH_COGNITO_ADMIN_SELF: () => {
    return `/v1/admins/mpl/cognito/self`;
  },
  GET_COGNITO_ADMIN_SELF: () => {
    return `/v1/admins/mpl/cognito/self`;
  },
  PATCH_COGNITO_SELF: () => {
    return `/v1/users/mpl/cognito/self`;
  },

  GET_ASSOCIATE_SOFTWARE_TOKEN: () => {
    return `/v1/users/mpl/cognito/self/associatesoftwaretoken`;
  },

  PATCH_VERIFY_SOFTWARE_TOKEN: () => {
    return `/v1/users/mpl/cognito/self/verifysoftwaretoken`;
  },

  CALL_DOMAIN_APPROVER_REQUEST: (
    appId,
    submissionId,
    status,
    comment,
    forceUninstall = false
  ) => {
    return `/v1/admins/domain-approver/mpl/applications/${appId}/submissions/${submissionId}/${status}?comment=${comment}`;
  },

  SUBMIT_FOR_PRODUCTION: (
    appId,
    submissionId,
    status,
    comment,
    forceUninstall = false
  ) => {
    if (getConfig().REACT_APP_PORTAL === "seller") {
      if (status === "delist") {
        return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/${status}?forceUninstall=${forceUninstall}`;
      } else {
        return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/${status}`;
      }
    } else if (
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      if (comment !== undefined)
        return `/v1/admins/domain-approver/mpl/applications/${appId}/submissions/${submissionId}/${status}?comment=${comment}`;
    }
  },

  SUBMIT_FOR_PRODUCTION_DOMAIN_APPROVER: (
    appId,
    submissionId,
    status,
    comment
  ) => {
    if (
      (getConfig().REACT_APP_PORTAL === "domain-approver" &&
        status !== "relist") ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      if (comment !== undefined)
        return `/v1/admins/mpl/applications/${appId}/submissions/${submissionId}/${status}?comment=${comment}`;
    }
  },

  SEND_SUBMISSION_FOR_PRODUCTION_DELIST_AND_RELIST: (
    appId,
    submissionId,
    status,
    forceUninstall
  ) => {
    if (getConfig().REACT_APP_PORTAL === "seller") {
      if (status === "delist") {
        return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/${status}?forceUninstall=${forceUninstall}`;
      } else {
        return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/${status}`;
      }
    } else if (
      (getConfig().REACT_APP_PORTAL === "domain-approver" &&
        status !== "relist") ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      if (forceUninstall !== undefined)
        return `/v1/admins/mpl/applications/${appId}/submissions/${submissionId}/${status}?forceUninstall=${forceUninstall}`;
    } else if (
      getConfig().REACT_APP_PORTAL === "domain-approver" &&
      status === "relist"
    )
      return `/v1/admins/mpl/applications/${appId}/submissions/${submissionId}/${status}`;
  },
  POST_NOTIFY_DEVELOPER: () => {
    return `/v2/admins/mpl/notification/approve_app`;
  },
  POST_FILTER_REASSIGN_OWNERSHIP: ({
    pageNumber = 0,
    pageSize = 10,
    sortBy = "USER_NAMES",
    sortOrder = "ASC",
    ignoreCase = true,
    searchType = "CONTAINS",
  }) => {
    return `/v1/admins/mpl/cognito/users/filter?pageNumber=${pageNumber}&pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}&ignoreCase=${ignoreCase}&searchType=${searchType}`;
  },

  SUBMIT_FOR_PRODUCTION_RELIST_AND_REVIEW: (appId, submissionId, status) => {
    return `/v1/admins/mpl/applications/${appId}/submissions/${submissionId}/${status}`;
  },

  UPLOAD_BANNER_FILE: (appId, submissionId, defaultLanguage) => {
    return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/banners?language=${defaultLanguage}`;
  },
  DELETE_BANNER_FILE: (appId, submissionId, bannerId, defaultLanguage) => {
    return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/banners/${bannerId}?language=${defaultLanguage}`;
  },
  UPLOAD_NEW_ICON_FILE: (appId, submissionId) => {
    return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/icon`;
  },
  UPLOAD_ATTACHMENT_FILE: (appId, submissionId) => {
    return `/v1/mpl/applications/${appId}/submissions/${submissionId}/attachment?filetype=ATTACHMENTS`;
  },
  DELETE_ATTACHMENT_FILE: (appId, submissionId, fileName) => {
    return `/v1/mpl/applications/${appId}/submissions/${submissionId}/attachment?fileName=${fileName}`;
  },
  CALLING_APPLICATION_SUBMISSIONS: (appId) => {
    return `/v1/users/mpl/applications/${appId}/submissions`;
  },
  UPLOAD_BINARY_FILE: (appId, submissionId) => {
    return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/binaries`;
  },
  UPLOAD_SIGNED_BINARY_FILE: (appId, submissionId) => {
    return `/v1/mpl/applications/${appId}/submissions/${submissionId}/binaries`;
  },

  DELETE_APPLICATION: (applicationId, submissionId) => {
    return `/v1/users/mpl/applications/${applicationId}/submissions/${submissionId}`;
  },

  DELETE_FEATURE: (featureId) => {
    return `/v1/users/mpl/applications/${featureId}`;
  },

  GET_ALL_SUBMISSIONS: (applicationId) => {
    if (
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin"
    ) {
      return `/v1/admins/mpl/applications/${applicationId}/submissions`;
    } else {
      return `/v1/users/mpl/applications/${applicationId}/submissions`;
    }
  },

  PUT_ADD_TEAM_MEMBER: (teamId) => {
    return `/v1/teams/${teamId}`;
  },

  DELETE_ADD_TEAM_MEMBER: (teamId) => {
    return `/v1/teams/${teamId}`;
  },

  PATCH_ADD_TEAM_MEMBER: (teamId) => {
    return `/v1/teams/${teamId}`;
  },

  POST_SELLER_DASHBOARD_TOTAL_DATA: (packageName, startDate, endDate) => {
    return `/v1/users/mpl/reports/total_data/${packageName}?startDate=${startDate}&endDate=${endDate}`;
  },

  GET_SELLER_DASHBOARD_OVERVIEW_DATA: (packageName) => {
    return `/v1/users/mpl/reports/app_overview_data/${packageName}`;
  },

  POST_SELLER_DASHBOARD_INSTALLS_DATA: (packageName, startDate, endDate) => {
    return `/v1/users/mpl/reports/periodic_data/${packageName}?startDate=${startDate}&endDate=${endDate}`;
  },

  POST_SELLER_CRASH_OVERVIEW_DATA: (packageName, startDate, endDate) => {
    return `/v1/users/mpl/reports/crash_data/overview/${packageName}?startDate=${startDate}&endDate=${endDate}`;
  },

  POST_SELLER_CRASH_STACKTRACE_DATA: (packageName, startDate, endDate) => {
    return `/v1/users/mpl/reports/crash_data/stacktrace_report/${packageName}?startDate=${startDate}&endDate=${endDate}`;
  },

  GET_CLUSTERS: () => {
    return `/v1/clusters`;
  },
  GET_LEGAL_DOCS: () => {
    return `/v1/legaldocumenttypes`;
  },
  DELETE_BINARY_KEY: (appId, submissionId, binaryKey) => {
    return `/v1/mpl/applications/${appId}/submissions/${submissionId}/binaries/${binaryKey}`;
  },
  DELETE_DESCRIPTIONS: (appId, submissionId) => {
    return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}/descriptions`;
  },
  GET_USER_DATA: (userId, teamId) => {
    return `/v1/users/mpl/cognito/users/${userId}?teamId=${teamId}`;
  },
  GET_GLOBAL_ADMIN_USERS: () => {
    return `/v1/admins/mpl/cognito/users/filter?pageNumber=0&pageSize=20&sortBy=USER_NAMES&sortOrder=ASC&ignoreCase=true&searchType=CONTAINS`;
  },
  GET_GLOBAL_ADMIN_USER_BY_ID: (userId) => {
    return `/v1/admins/mpl/cognito/users/${userId}`;
  },
  GET_GLOBAL_ADMIN_OVER_VIEW: (
    isAppListNeeded,
    isCountNeeded,
    isAppCountNeeded,
    offset,
    limit
  ) => {
    return `/v2/admins/mpl/catalog/overview/applications?pageNumber=${offset}&pageSize=${limit}`;
  },

  GET_GLOBAL_ADMIN_DETAILS: () => {
    return `/v2/admins/mpl/catalog/overview`;
  },

  GET_GLOBAL_ADMIN_DOWNLOAD_DETAILS: () => {
    return `/v2/admins/mpl/catalog/overview/downloadreport`;
  },

  GET_GLOBAL_ADMIN_FLEX_METADATA: () => {
    return `/v1/admins/global-admin/mpl/dictionaries/labels?domain=NONE`;
  },

  CREATE_GLOBAL_ADMIN_FLEX_METADATA: () => {
    return `/v1/admins/global-admin/mpl/dictionaries/labels`;
  },

  UPDATE_GLOBAL_ADMIN_FLEX_METADATA: () => {
    return `/v1/admins/global-admin/mpl/dictionaries/labels`;
  },

  DELETE_GLOBAL_ADMIN_FLEX_METADATA: (id) => {
    return `/v1/admins/global-admin/mpl/dictionaries/labels?id=${id}`;
  },

  GET_GLOBAL_ADMIN_FLEX_SUBMISSION_API_DATA: (appId, submissionId) => {
    return `/v1/admins/mpl/applications/${appId}/submissions/${submissionId} `;
  },

  SET_GLOBAL_ADMIN_FLEX_SUBMISSION_API_DATA: (appId, submissionId) => {
    return `/v1/users/mpl/applications/${appId}/submissions/${submissionId}`;
  },

  GET_APPLICATION_SUMMARY_GLOBAL_ADMIN: () => {
    return `/v1/admins/global-admin/mpl/applications/adminreport`;
  },
  TOGGLE_MFA: (id) => {
    return `/v1/admins/mpl/cognito/users/${id}`;
  },
  GET_ACCEPT_USER_DOMAINS: () => {
    return `/v1/admins/global-admin/mpl/domains`;
  },

  GET_ACCEPT_USER_COMPANIES: () => {
    return `/v1/companies`;
  },

  ADD_NEW_COMPANY: () => {
    return `/v1/companies/createcompany`;
  },

  ACCEPT_USER_REQUEST: (id) => {
    return `/v2/admins/global-admin/mpl/users/${id}/activate`;
  },

  REJECT_USER_REQUEST: (id) => {
    return `/v1/admins/global-admin/mpl/users/${id}/reject`;
  },

  DEACTIVATE_USER: (id) => {
    return `/v1/admins/global-admin/mpl/users/${id}/deactivate`;
  },

  REACTIVATE_USER: (id) => {
    return `/v1/admins/global-admin/mpl/users/${id}/reactivate`;
  },

  DELETE_GLOBAL_ADMIN_USER: (
    id,
    checkPurgeData = false,
    forceUninstall = false
  ) => {
    return `/v1/admins/global-admin/mpl/users/${id}/data?purgeData=${checkPurgeData}&forceUninstall=${forceUninstall}`;
  },

  GET_ACCEPT_USER_OEM_ADMIN_ROLES: () => {
    return `/v1/admins/global-admin/mpl/oem_admin_roles`;
  },

  GET_OVER_VIEW_DATA: (
    isAppListNeeded,
    isCountNeeded,
    isAppCountNeeded,
    offset,
    limit
  ) => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (portfolioId === null) {
      let roles = JSON.parse(sessionStorage.getItem("roles"));
      if (getConfig().REACT_APP_PORTAL === "oem-admin") {
        let checkPortfolioId = roles[0][0];
        let prefix = "VWAG_STORE_BRAND_ADMIN_";
        let suffix = "_READONLY";
        if (checkPortfolioId.includes(prefix)) {
          checkPortfolioId = checkPortfolioId.slice(prefix.length);
        }
        if (checkPortfolioId.includes(suffix)) {
          // Handle read-only portfolio case
          checkPortfolioId = checkPortfolioId.slice(0, -suffix.length);
        }
        return `/v1/portfoliomanager/portfolios/${checkPortfolioId}/catalog/overview?isAppListNeeded=${isAppListNeeded}&isCountNeeded=${isCountNeeded}&isAppCountNeeded=${isAppCountNeeded}&offset=${offset}&limit=${limit}`;
      }
    } else {
      return `/v1/portfoliomanager/portfolios/${portfolioId}/catalog/overview?isAppListNeeded=${isAppListNeeded}&isCountNeeded=${isCountNeeded}&isAppCountNeeded=${isAppCountNeeded}&offset=${offset}&limit=${limit}`;
    }
  },

  GET_SETTING_RESPONSE: () => {
    return `/v1/portfoliomanager/settings`;
  },
  GET_PORTAL_CONFIG_GLOBAL_ADMIN: () => {
    return `/v1/portalconfig`;
  },
  GET_OEM_APP_OVERVIEW_DATA: (contentId) => {
    return `/v1/portfoliomanager/portfolios/${sessionStorage.getItem(
      "portfolioId"
    )}/contents/${contentId}/overview`;
  },

  GET_GLOBAL_APP_OVERVIEW_DATA: (contentId) => {
    return `/v2/admins/mpl/applications/${contentId}/submissions`;
  },

  GET_GLOBAL_ADMIN_ANALYTICS_DATA: (contentId) => {
    return `/v1/users/mpl/reports/total_data/${contentId}`;
  },

  GET_OEM_APP_BASED_ON_VERSION_CODE: (contentId, versionCode) => {
    return `/v1/portfoliomanager/portfolios/${sessionStorage.getItem(
      "portfolioId"
    )}/contents/${contentId}/overview?versionCode=${versionCode}`;
  },

  GET_GLOBAL_APP_BASED_ON_VERSION_CODE: (contentId, versionCode) => {
    return `/v2/admins/mpl/applications/${contentId}/submissions/${versionCode}`;
  },

  GET_USER_INFO: () => {
    return `/v1/users/mpl/cognito/self`;
  },
  GET_ALL_IN_APP: (sellerId, packageId, skipInvalidProducts) => {
    return skipInvalidProducts
      ? "/v1/product/list?sellerId=" +
          sellerId +
          "&packageId=" +
          packageId +
          "&skipInvalidProducts=" +
          skipInvalidProducts
      : "/v1/product/list?sellerId=" +
          sellerId +
          "&packageId=" +
          packageId +
          "&skipInvalidProducts=false";
  },
  GET_EXCHANGE_RATES: (basePrice, currencyCode) => {
    return (
      "/v1/product/exchangeprice?basePrice=" +
      basePrice +
      "&currencyCode=" +
      currencyCode
    );
  },
  POST_ADD_PRODUCT: () => {
    return "/v1/product";
  },
  POST_ADD_PRODUCT_ENABLE_RECURRENCE: () => {
    return "/v1/product?enableRecurrence=true";
  },
  GET_ONE_TIME_PRODUCT: (productId) => {
    return "/v1/product/details?id=" + productId;
  },
  GET_RECURRING_PRODUCT: (productId) => {
    return "/v1/product/details?id=" + productId + "&enableRecurrence=true";
  },
  PUT_UPDATE_PRODUCT_ENABLE_RECURRENCE: (id) => {
    return "/v1/product?id=" + id + "&enableRecurrence=true";
  },
  PUT_UPDATE_PRODUCT_ONE_TIME: (id) => {
    return "/v1/product?id=" + id;
  },
  POST_ADD_PLAN_IN_APP: () => {
    return "/v1/product/plan";
  },
  GET_PLAN_DETAILS: (productId, planId) => {
    return (
      "/v1/product/plan/details?productId=" + productId + "&planId=" + planId
    );
  },
  GET_ALL_PREMIUM_APP: (sellerId, packageId, skipInvalidProducts) => {
    return skipInvalidProducts
      ? "/v1/product/list?sellerId=" +
          sellerId +
          "&packageId=" +
          packageId +
          "&skipInvalidProducts=" +
          skipInvalidProducts +
          "&productType=APP"
      : "/v1/product/list?sellerId=" +
          sellerId +
          "&packageId=" +
          packageId +
          "&skipInvalidProducts=false&productType=APP";
  },
  GET_CONTENTS_DATA: (status, isCountNeeded) => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    return `/v2/portfoliomanager/portfolios/${portfolioId}/contents?status=${status}&isCountNeeded=${isCountNeeded}`;
  },
  GET_ADMIN_COUNTRIES: () => {
    return `/v1/portfoliomanager/country`;
  },

  GET_GLOBAL_ADMIN_COUNTRIES: () => {
    return `/v1/admins/mpl/dictionaries/countries?cvaaexclusion=false`;
  },

  GET_ADMIN_CATEGORIES: () => {
    return `/v1/admins/mpl/dictionaries/categories`;
  },

  POST_CREATE_CATEGORY: () => {
    return `/v2/admins/mpl/dictionaries/categories`;
  },

  PUT_UPDATE_CATEGORY: (categoryId) => {
    return `/v2/admins/mpl/dictionaries/categories/${categoryId}`;
  },

  POST_CATEGORY_ICON: (categoryId) => {
    return `/v2/admins/mpl/dictionaries/categories/${categoryId}/icon`;
  },

  DELETE_CATEGORY: (categoryId) => {
    return `/v2/admins/mpl/dictionaries/categories/${categoryId}`;
  },

  GET_ADMIN_OEM: () => {
    return `/v2/admins/mpl/dictionaries/mmy`;
  },

  GET_MMY_YEARS: () => {
    return `/v2/admins/mpl/dictionaries/mmy/year`;
  },

  POST_ADD_MMY: () => {
    return `/v2/admins/mpl/dictionaries/mmy`;
  },

  PUT_ADD_MMY: (manufacturerId, modelId) => {
    if (modelId) {
      return `/v2/admins/mpl/dictionaries/mmy/${manufacturerId}/${modelId}`;
    }
    return `/v2/admins/mpl/dictionaries/mmy/${manufacturerId}`;
  },

  GET_ADMIN_DOMAIN: () => {
    return `/v1/admins/global-admin/mpl/domains`;
  },

  GET_ADMIN_CPU_TYPES: () => {
    return `/v1/admins/mpl/dictionaries/cpus`;
  },

  GET_GLOBAL_ADMIN_ECUTYPES: () => {
    return `/v1/dictionaries/ecus`;
  },

  GET_PORTFOLIO_CONTENTS_DETAILS: (portfolioId, contentId) => {
    if (getConfig().REACT_APP_PORTAL === "domain-approver") {
      return `/v1/domainapprover/contents/${contentId}`;
    } else {
      return `/v1/portfoliomanager/portfolios/${portfolioId}/contents/${contentId}`;
    }
  },

  PATCH_PORTFOLIO_CONTENTS_DETAILS: (portfolioId, contentId) => {
    return `/v2/portfoliomanager/portfolios/${portfolioId}/contents/${contentId}`;
  },

  POST_TABLEAU_URL: () => {
    return `/v1/portfoliomanager/portfolios/${sessionStorage.getItem(
      "portfolioId"
    )}/tableauTicket`;
  },

  POST_TABLEAU_URL_GLOBAL_ADMIN: () => {
    return `/v1/admins/mpl/tableau/ticket`;
  },

  GET_ADMIN_ECU_TYPES: () => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    return `/v1/portfoliomanager/portfolios/${portfolioId}/contents/ecutypes`;
  },

  // Domain Approver specific endpoints
  GET_DOMAIN_APPROVER_ECU_TYPES: () => {
    const portfolioId = sessionStorage.getItem("domain");
    console.log("portfolioId=====>", portfolioId);
    return `/v1/dictionaries/ecus?domain=${portfolioId}`;
  },

  GET_DOMAIN_APPROVER_COUNTRIES: () => {
    return `/v1/admins/mpl/dictionaries/countries?cvaaexclusion=false`;
  },

  GET_OEMADMIN_ECUTYPES: () => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    return `/v1/portfoliomanager/portfolios/${portfolioId}/contents/ecutypes`;
  },

  POST_ADD_PLAN_PREMIUM: () => {
    return "/v1/product/plan?productType=APP";
  },
  PUT_UPDATE_PLAN_PREMIUM: (planId) => {
    return `/v1/product/plan?id=${planId}&productType=APP`;
  },
  DELETE_APP_PRICING: (packageId) => {
    return `/v1/product/${packageId}`;
  },
  GET_OEM_ADMIN_CATEGORIES: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/getcategories`;
  },
  PATCH_OEM_ADMIN_CATEGORIES: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/updatecategories`;
  },
  GET_OEM_SETTINGS_EMAIL_NOTIFICATION: () => {
    return `/v1/portfoliomanager/portfolios/${sessionStorage.getItem(
      "portfolioId"
    )}/getemailrecipients`;
  },
  PUT_NEW_EMAIL_ADDRESS_FOR_OEM_SETTINGS: (emailRecipients) => {
    return `/v1/portfoliomanager/portfolios/${sessionStorage.getItem(
      "portfolioId"
    )}/notificationemailregistration`;
  },

  GET_AGE_RATING: () => {
    return `/v1/users/mpl/dictionaries/ageratings`;
  },
  GET_OEM_ADMIN_RECOMMENDED_APPS: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/recommendedapps`;
  },
  GET_OEM_ADMIN_SUGGESTED_APPS: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/recommendedapps?isWelcomeWizard=true`;
  },
  GET_OEM_ADMIN_ALL_COUNTRIES: () => {
    return `/v1/portfoliomanager/country`;
  },
  GET_OEM_ADMIN_SEARCH_APPS: (
    portfolio,
    locale,
    searchText,
    countries,
    ecuTypes
  ) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/recommendedapps/search?locale=${locale}&searchtext=${searchText}&countries=${countries}&ecuTypes=${ecuTypes}`;
  },
  GET_OEM_ADMIN_SUGGESTED_SEARCH_APPS: (
    portfolio,
    locale,
    searchText,
    countries,
    ecuTypes
  ) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/recommendedapps/search?locale=${locale}&searchtext=${searchText}&countries=${countries}&ecuTypes=${ecuTypes}&isWelcomeWizard=true`;
  },
  PATCH_OEM_ADMIN_ADD_RECOMMENDED_APP: (portfolio, packageId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/recommendedapps/${packageId}`;
  },
  PATCH_OEM_ADMIN_ADD_SUGGESTED_APP: (portfolio, packageId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/recommendedapps/${packageId}?isWelcomeWizard=true`;
  },
  DELETE_OEM_ADMIN_RECOMMENDED_APP: (portfolio, packageId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/recommendedapps/${packageId}`;
  },
  DELETE_OEM_ADMIN_SUGGESTED_APP: (portfolio, packageId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/recommendedapps/${packageId}?isWelcomeWizard=true`;
  },

  PATCH_OEM_ADMIN_APPROVED_REJECT_APP: (portfolioId, contentId) => {
    return `/v2/portfoliomanager/portfolios/${portfolioId}/contents/${contentId}`;
  },
  PATCH_DOMAIN_APPROVER_APPROVED_REJECT_APPS: (contentId) => {
    return `/v2/domainapprover/contents/${contentId}`;
  },
  GET_OEM_ADMIN_TARGET_ECUTYPES: (portfolioId, contentId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/contents/${contentId}/ecutypes`;
  },

  GET_OEM_ADMIN_APPROVAL_HISTORY: (portfolioId, contentId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/contents/${contentId}/approvalHistory`;
  },

  GENERATE_CLIENT: (appId) => {
    return `/v1/applications/${appId}/client`;
  },
  GET_APP_PRICING: (sellerId, packageName, type) => {
    return (
      "/v1/product/portfoliomanager/list?sellerId=" +
      sellerId +
      "&packageId=" +
      packageName +
      "&productType=" +
      type
    );
  },
  GET_PRODUCT_DETAILS: (id, enableRecurrence) => {
    return (
      "/v1/product/portfoliomanager/details?id=" +
      id +
      "&enableRecurrence=" +
      enableRecurrence
    );
  },
  GET_OEM_ADMIN_PLAN_DETAILS: (productId, planId) => {
    return (
      "/v1/product/portfoliomanager/plan/details?productId=" +
      productId +
      "&planId=" +
      planId
    );
  },
  GET_OEM_ADMIN_LOCALES: () => {
    return `/v1/portfoliomanager/locales`;
  },
  GET_OEM_LEGAL_DOCUMENT_INFOS: () => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    return `/v1/portfoliomanager/portfolios/${portfolioId}/legaldocumentinfos`;
  },
  PATCH_OEM_LEGAL_DOCUMENT_INFOS: (language) => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    return `/v1/portfoliomanager/portfolios/${portfolioId}/locales/${language}/legaldocumentinfos`;
  },
  GET_OEM_LEGAL_INFOS: () => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    return `/v1/portfoliomanager/portfolios/${portfolioId}/legalinfos`;
  },
  GET_DOMAIN_APPROVER_CONTENTS: () => {
    return `/v1/domainapprover/contents`;
  },
  POST_DOMAIN_APPROVER_CLUSTERS: () => {
    return `/v1/clusters`;
  },
  GET_DOMAIN_APPROVER_HEAD_UNIT: (domainAliasId) => {
    if (domainAliasId) {
      return `/v1/admins/mpl/dictionaries/headunits?domainAliasId=${domainAliasId}`;
    }
    return `/v1/admins/mpl/dictionaries/headunits`;
  },
  GET_OEM_ADMIN_VEHICLE_LISTS: (portfolio) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/groups`;
  },
  CREATE_OEM_ADMIN_VEHICLE_LIST: (portfolio) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/groups`;
  },
  UPLOAD_OEM_ADMIN_VEHICLE_LIST: (portfolio, groupId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/groups/${groupId}/members/upload?memberIdType=VIN`;
  },
  DELETE_OEM_ADMIN_VEHICLE_LIST: (portfolio, groupId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/groups/${groupId}`;
  },
  GET_OEM_ADMIN_VEHICLES_FOR_GROUP: (portfolio, groupId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/groups/${groupId}/members`;
  },
  POST_OEM_ADMIN_VEHICLES_FOR_GROUP: (portfolio, groupId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/groups/${groupId}/members?memberIdType=VIN`;
  },
  DELETE_OEM_ADMIN_VEHICLES_FROM_GROUP: (portfolio, groupId, memberId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/groups/${groupId}/members/${memberId}`;
  },
  POST_OEM_ADMIN_GROUP_TO_APPLICATION: (portfolio, packageId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/packageId/${packageId}/groups`;
  },
  PATCH_OEM_ADMIN_GROUP_TO_APPLICATION: (portfolio, groupId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/groups/${groupId}`;
  },
  GET_COGNITO_USERS: (queryParams) => {
    return `/v1/admins/mpl/cognito/users/filter?${queryParams}`;
  },
  GET_GLOBAL_ADMIN_PUBLISHER_INFO_COGNITO: (paymentID) => {
    return `/v1/admins/mpl/cognito/users/${paymentID}`;
  },

  GET_OEM_ADMIN_GROUP_FOR_APPLICATION: (portfolio, packageId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/packageId/${packageId}/groups`;
  },
  DELETE_OEM_ADMIN_GROUP_FROM_APPLICATION: (portfolio, groupId, packageId) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/packageId/${packageId}/groups/${groupId}`;
  },
  GET_WELCOME_WIZARD_CAMPAIGN: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/campaigns`;
  },
  POST_GET_APPS_WELCOME_WIZARD_CAMPAIGN: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/campaigns/search`;
  },
  GET_CLUSTER_NUMBER_IDP: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/contents/clusternumber`;
  },
  GET_MMY_FOR_PORTFOLIO: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/contents/mmy`;
  },
  POST_CREATE_CAMPAIGN: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/campaign`;
  },
  GET_APPS_FOR_CAMPAIGN: (portfolioId, campaignName) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/campaigns/${campaignName}/apps`;
  },
   GET_REORDER_APPS_FOR_CAMPAIGN: (portfolioId, campaignName) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/campaigns/${campaignName}/apps/reorder`;
  },
  GET_FLEX_META_DATA_LABELS: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/labels`;
  },
  POST_FLEX_META_DATA_LABELS: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/label`;
  },
  DELETE_FLEX_META_DATA_LABEL: (portfolioId, title) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/label/${title}`;
  },
  PUT_DSA_URL: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/portfoliosettings`;
  },
  GET_DSA_URL: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/portfoliosettings`;
  },
  PATCH_DSA_URL: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/portfoliosettings`;
  },
  DELETE_DSA_URL: (portfolioId, propertyKey) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/portfoliosettings/properties/${propertyKey}`;
  },
  PATCH_ADD_APPS_WELCOME_WIZARD_CAMPAIGN: (portfolioId, campaignName) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/campaigns/${campaignName}`;
  },
  DELETE_APP_WELCOME_WIZARD_CAMPAIGN: (
    portfolioId,
    campaignName,
    packageId
  ) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/campaigns/${campaignName}/apps/${packageId}`;
  },
  DELETE_CAMPAIGN_APP_WELCOME_WIZARD: (portfolioId, campaignName) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/campaigns/${campaignName}`;
  },
  GET_OEM_ADMIN_CONTENTS_MMY: (portfolioId) => {  
    return `/v1/portfoliomanager/portfolios/${portfolioId}/contents/mmy`;
  },
  GET_OEM_ADMIN_ECUTYPE_MAPPINGS_URL: (portfolioId) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/portfoliosettings?fetchEcuTypeMappings=true`;
  },

  DELETE_OEM_ADMIN_ECUTYPE_MAPPINGS_URL: (portfolioId, propertyKey) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/portfoliosettings/properties/${propertyKey}?deleteEcuTypeMapping=true`;
  },
  
  PUT_REORDER_RECOMMENDED_APPS: (portfolio) => {
    return `/v1/portfoliomanager/portfolios/${portfolio}/recommendedapps/reorder`;
  },
  GET_DEPENDENCY_SEARCH: (portfolioId, searchText, locale) => {
    return `/v1/portfoliomanager/portfolios/${portfolioId}/dependency/search?searchtext=${searchText}&locale=${locale}`;
  }
};
export default ENDPOINTS;
